/* eslint no-await-in-loop: "off" */
import React, { useState } from "react";
import { CircularProgress, Input } from "@nextui-org/react";
import { IoChevronDown, IoSearch, IoCallOutline, IoPersonOutline } from "react-icons/io5";
import { useSelector } from "react-redux";
import { MdMailOutline, MdOutlineSimCardDownload } from "react-icons/md";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { SlOrganization } from "react-icons/sl";

import { GradeSliderDropdown } from "../../../components/Navbar";
import CalendarDropdown from "../../../components/CalendarDropdown";
import CallDurationDropdown from "../../../components/CallDurationDropdown";

import { SubjectSheetIcon, UserGroupIcon } from "../../../assets/icons/audio-icons";
import {
  transformFromUTC,
  transformFromUTCFromISO,
  transformToUTC,
} from "../../../utils/dateUtils";
import { useAppContext } from "../../../contexts/AppContext";
import MHoverToggleMenu from "../../../components/Kit/MHoverToggleMenu";
import getConfig from "../../../conf/config";
import myAxios from "../../../utils/axiosWrapper";
import { downloadCallAudioBlob } from "../../../utils/downloadCallAudio";

const CallSearchBar = ({
  onTogglePageFilter,
  setChoiceFilterParam,
  pageFilters,
  organisations,
  operators,
  isOperatorsLoading,
  company_config,
  gradeValue,
  setGradeValue,
  frustrationGradeValue,
  teamGrades,
  individualGrades,
  display_tz,
  sortDescriptor,
  isOrganisationsLoading,
  callDurationRange,
  setCallDurationRange,
}) => {
  const {
    // debouncedSearchText: searchText,
    searchText,
    setSearchText,
    handleOnGradeChange,
    callsDateRange,
    setCallsDateRange,
  } = useAppContext();
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isExportAllLoading, setIsExportAllLoading] = useState(false);
  const { calls, selectedCalls = [] } = useSelector((state) => state.calls.calls);
  const { t } = useTranslation();

  const incoming_call_purposes = company_config?.incoming_call_purposes || [];
  const outgoing_call_purposes = company_config?.outgoing_call_purposes || [];

  const call_purposes = [...new Set([...incoming_call_purposes, ...outgoing_call_purposes])];

  const pageFilterToggled = pageFilters
    .filter((item) => item?.params?.length > 0)
    .map((item) => item.type);
  const nbSelectedCalls = selectedCalls.length;
  const hasSelectedCalls = nbSelectedCalls > 0;

  const formatMultiCallFileName = (fileExtension) => {
    const dateBloc = new Date()
      .toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/,/g, "")
      .replace(/:/g, "_");

    return `export_${dateBloc}.${fileExtension}`;
  };

  const downloadAudioFile = async (callId) => downloadCallAudioBlob(callId);

  const formatAudioFileName = (item) => {
    const dateBloc = new Date(item.call_timestamp * 1000)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/,/g, "")
      .replace(/:/g, "_");

    return `${item?.operator_name.toUpperCase()}_${dateBloc}.wav`;
  };

  const exportAudioFilesZipped = async () => {
    if (!selectedCalls || selectedCalls.length <= 0) {
      return;
    }
    if (isExportLoading) {
      return;
    }
    const zipFilename = formatMultiCallFileName("zip");
    const zip = new JSZip();
    let cpt = 0;
    setIsExportLoading(true);
    try {
      while (cpt < selectedCalls.length) {
        const selectedCall = selectedCalls[cpt];
        const filename = formatAudioFileName(selectedCall);
        const audioBlob = await downloadAudioFile(selectedCall.id);
        zip.file(filename, audioBlob, { binary: true });
        cpt++;
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, zipFilename);
    } catch (error) {
      console.error("Failed to download audio files:", error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleOnExport = () => {
    // const filename = `calls_${format(new Date(dateRangeToUse.startDate), "MM-dd-yyyy")}_${format(
    //   new Date(dateRangeToUse.endDate),
    //   "MM-dd-yyyy"
    // )}.csv`;
    // parseArrayToCsv(hasSelectedCalls ? selectedCalls : calls, filename);
    exportAudioFilesZipped();
  };

  const handleOnExportAll = async () => {
    try {
      setIsExportAllLoading(true);
      const isFrustratedToggled = pageFilterToggled.includes("frustrated");
      // /algolia_calls/export
      const filters = {
        start_date: callsDateRange.startDate,
        end_date: callsDateRange.endDate,
        ...teamGrades,
        ...individualGrades,
        min_frustration_grade: isFrustratedToggled ? frustrationGradeValue[0] : 0,
        max_frustration_grade: isFrustratedToggled ? frustrationGradeValue[1] : 10,
      };

      if (filters?.start_date) {
        filters.start_date = transformToUTC(filters?.start_date, display_tz);
      }

      if (filters?.end_date) {
        filters.end_date = transformToUTC(filters?.end_date, display_tz);
      }

      const isLiveContentSearch = false;

      const response = await myAxios.post(
        `${getConfig().API_URL}/audio/api/v1/calls/algolia_calls/export`,
        {
          searchText,
          filters,
          sortDescriptor,
          isLiveContentSearch,
          widgetFilters: pageFilters,
        },
        {
          responseType: "blob",
        }
      );

      const now = new Date();
      const formattedDate = now.toISOString().split("T")[0];
      const searchPart = searchText ? `_${searchText.substring(0, 20).replace(/\s+/g, "_")}` : "";
      const filename = `calls_export${searchPart}_${formattedDate}.xlsx`;

      // Create Blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up
      window.URL.revokeObjectURL(url);

      setIsExportAllLoading(false);
    } catch (error) {
      console.error("Failed to export calls:", error);
      setIsExportAllLoading(false);
    }
  };

  const onShareSelected = () => {
    if (!hasSelectedCalls) {
      return;
    }

    const baseUrl = getConfig().BASE_URL;

    const links = selectedCalls.map((selectedCall) => {
      return `- ${baseUrl}/calls/${selectedCall?.id}`;
    });

    const mailString = links.join("\n");

    const mailtoLink = `mailto:?subject=Partage de Calls Audio&body=${encodeURIComponent(
      `Voici les calls :
${mailString}`
    )}`;
    window.open(mailtoLink, "_blank");
  };

  const onShareSelectedXLSX = () => {
    if (!hasSelectedCalls) {
      return;
    }

    // const baseUrl = getConfig().BASE_URL;
    const baseUrl = "https://audio.app.easierlabs.ai";

    const rows = selectedCalls?.map((selectedCall) => {
      // const audioUrl = selectedCall?.audio_url;
      const callLink = `${baseUrl}/calls/${selectedCall?.id}`;
      const localisationsById = {
        "9a8j5obKmJbBOvEoOyID": "MAURICE",
        AoREEREk93cN3bXetXOH: "CROSNE",
        wMrxVxAlshoS9LzAe9RA: "JOUE-LES-TOURS",
      };

      const localisation_name = localisationsById?.[selectedCall?.localisation_id] || "";

      let callDate = "";
      if (selectedCall?.call_timestamp) {
        if (Number.isInteger(selectedCall.call_timestamp)) {
          callDate = format(
            transformFromUTC(selectedCall.call_timestamp, "Europe/Paris"),
            "yyyy-MM-dd"
          );
        } else {
          callDate = format(
            transformFromUTCFromISO(selectedCall.call_timestamp, "Europe/Paris"),
            "yyyy-MM-dd"
          );
        }
      }

      const frustrationThemeMap = {
        order_not_received: "Commande non reçue",
        product_out_of_stock_discontinued: "Produit indisponible",
        pickup_not_completed: "Collecte non effectuée",
        duplicate_request: "Demande de duplicata",
        invoice_explanation: "Explication de facture",
        client_callback_not_made: "Rappel client manqué",
        equipment_repair_delay: "Retard de réparation",
        pharmacy_anesthetics: "Produits anesthésiques",
        product_information: "Information produit",
        order_for_individuals_students_employees: "Commande particuliers",
        software_it_support: "Support logiciel / IT",
        other: "Autre",
      };

      return {
        id: selectedCall.id,
        // audio_dl: { f: `HYPERLINK("${audioUrl}")` },
        date: callDate,
        call_link: { f: `HYPERLINK("${callLink}")` },
        operator_name: selectedCall?.operator_name || "",
        client_number: selectedCall?.client_number || "",
        subject: selectedCall?.analysis?.call_purpose || "",
        call_summary: selectedCall?.analysis?.call_summary || "",
        // from: selectedCall.incoming ? "incoming" : "outgoing",
        // grade: selectedCall?.analysis?.call_overall_grade?.toFixed(1) || -1,
        // dynamism: selectedCall?.analysis?.dynamism || -1,
        // call_structure_respect: selectedCall?.analysis?.call_structure_respect || -1,
        client_sentiment: selectedCall?.analysis?.client_sentiment || "",
        // customer_service_tone: selectedCall?.analysis?.customer_service_tone || "",
        // team: selectedCall?.team || "",
        department: selectedCall?.department || "",
        time: selectedCall?.call_timestamp
          ? Number.isInteger(selectedCall.call_timestamp)
            ? format(transformFromUTC(selectedCall.call_timestamp, "Europe/Paris"), "HH:mm:ss")
            : format(
                transformFromUTCFromISO(selectedCall.call_timestamp, "Europe/Paris"),
                "HH:mm:ss"
              )
          : "",
        SITE: localisation_name,
        frustration_theme: frustrationThemeMap?.[selectedCall?.analysis?.frustration_theme] || "",
        frustration_grade: selectedCall?.analysis?.frustration_grade || "",
        type: selectedCall?.incoming ? "entrant" : "sortant",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Calls");
    const fileName = formatMultiCallFileName("xlsx");
    XLSX.writeFile(workbook, fileName, { compression: false });
  };

  const renderChoiceFilter = (quickFilter) => {
    const isSelected = pageFilterToggled.includes(quickFilter?.key);
    const htmlKey = `f-${quickFilter.key}`;
    const filterData = isSelected
      ? pageFilters.find((item) => item.type === quickFilter?.key)
      : { params: [] };

    const valueToFilter = quickFilter?.valueToFilter;

    return (
      <MHoverToggleMenu
        key={htmlKey}
        triggerComponent={
          <div
            className="cursor-pointer flex items-center rounded-md px-[12px] py-[8px] h-[36px] gap-[8px]"
            // onClick={() => onTogglePageFilter(quickFilter.key, valueToFilter)}
            // onKeyDown={() => onTogglePageFilter(quickFilter.key, valueToFilter)}
          >
            <quickFilter.icon
              size={16}
              color={isSelected ? "#4318FF" : "#737373"}
            />
            <span className=" text-sm whitespace-nowrap">{t(quickFilter.label)}</span>
            <IoChevronDown
              size={16}
              color={isSelected ? "#4318FF" : "#737373"}
            />
          </div>
        }
        // preventToggle={isSelected}
        items={quickFilter.choices}
        isMultiSelect
        selectedIds={filterData?.params || []}
        setSelectedId={setChoiceFilterParam(quickFilter.key)}
      />
    );
  };

  const renderAction = (actionLabel) => {
    switch (actionLabel) {
      case "grade":
        return (
          <GradeSliderDropdown
            label="Grade"
            sliderLabel="Select grade"
            value={gradeValue}
            onChange={(gradeValues) => {
              setGradeValue(gradeValues);
              handleOnGradeChange("individual", {
                min_individual_grade: gradeValues[0],
                max_individual_grade: gradeValues[1],
                exclude_na: !!individualGrades?.exclude_na,
              });
            }}
            excludeNA={!!individualGrades?.exclude_na}
            onExcludeNAChange={(newExcludeNA) => {
              handleOnGradeChange("individual", {
                min_individual_grade: gradeValue[0],
                max_individual_grade: gradeValue[1],
                exclude_na: newExcludeNA,
              });
            }}
          />
        );
      case "dates":
        return (
          <CalendarDropdown
            dateRangeValue={callsDateRange}
            onDateRangeChange={(dates) => {
              setCallsDateRange(dates);
            }}
            triggerClassName="bg-transparent"
          />
        );
      case "share":
        return (
          <div
            onClick={onShareSelected}
            onKeyDown={onShareSelected}
            className={`${
              hasSelectedCalls
                ? "cursor-pointer hover:bg-black/10 duration-400"
                : "cursor-not-allowed"
            } px-[4px] py-[8px] rounded-md flex items-center text-sm text-[#171717]/[0.6]`}
          >
            {hasSelectedCalls ? `${t("Share")} (${nbSelectedCalls})` : t("Share")}
            <MdMailOutline
              size={20}
              className="ml-2"
            />
          </div>
        );
      case "call_direction":
        return renderChoiceFilter({
          label: "Call Direction",
          key: "call_direction",
          icon: IoCallOutline,
          isChoiceFilter: true,
          choices: [
            {
              label: t("Incoming") || "Incoming",
              key: "incoming",
            },
            {
              label: t("Outgoing") || "Outgoing",
              key: "outgoing",
            },
          ],
        });
      case "call_duration":
        return (
          <CallDurationDropdown
            value={callDurationRange}
            onChange={setCallDurationRange}
          />
        );
      case "departments":
        if (isOrganisationsLoading) {
          return (
            <div className="px-[12px] h-[36px] flex items-center gap-[8px]">
              <UserGroupIcon
                size={16}
                color="#737373"
              />
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
                className="ml-2"
              />
            </div>
          );
        }

        return renderChoiceFilter({
          label: "Departments",
          key: "department",
          icon: UserGroupIcon,
          isChoiceFilter: true,
          choices: (organisations?.departments || [])?.map((department) => ({
            label: department.name,
            key: department.id,
          })),
        });
      case "operators": {
        if (isOperatorsLoading) {
          return (
            <div className="px-[12px] h-[36px] flex items-center gap-[8px]">
              <IoPersonOutline
                size={16}
                color="#737373"
              />
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
                className="ml-2"
              />
            </div>
          );
        }

        // Deactivated operators are not offered, unless already selected (e.g.
        // from a shared link) so they can still be unticked from the menu.
        const selectedOperatorIds =
          pageFilters.find((item) => item.type === "operators")?.params || [];

        return renderChoiceFilter({
          label: "Opérateurs",
          key: "operators",
          icon: IoPersonOutline,
          isChoiceFilter: true,
          choices: (operators || [])
            .filter(
              (operator) =>
                operator?.is_active !== false || selectedOperatorIds.includes(operator?.id)
            )
            .map((operator) => ({
              label: `${operator.first_name || ""} ${operator.last_name || ""}`.trim(),
              key: operator.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        });
      }
      case "subjects":
        return renderChoiceFilter({
          label: "Subject",
          key: "subject",
          icon: SubjectSheetIcon,
          isChoiceFilter: true,
          choices: call_purposes.map((item) => ({
            label: item,
            key: item,
          })),
        });
      case "export":
        return (
          <div
            onClick={handleOnExport}
            onKeyDown={handleOnExport}
            className={`${
              hasSelectedCalls
                ? "cursor-pointer hover:bg-black/10 duration-400"
                : "cursor-not-allowed"
            } px-[4px] py-[8px] rounded-md flex items-center text-sm text-[#171717]/[0.6]`}
          >
            {isExportLoading && (
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
                className="mr-[16px]"
              />
            )}
            {hasSelectedCalls ? `${t("Download")} (${nbSelectedCalls})` : t("Download")}
            <MdOutlineSimCardDownload
              size={20}
              color="#737373"
              className="ml-2"
            />
          </div>
        );
      case "export_all":
        return (
          <div
            onClick={handleOnExportAll}
            onKeyDown={handleOnExportAll}
            className="cursor-pointer hover:bg-black/10 duration-400 px-[4px] py-[8px] rounded-md flex items-center text-sm text-[#171717]/[0.6]"
          >
            {isExportAllLoading && (
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
                className="mr-[16px]"
              />
            )}
            {t("Export")}
            <MdOutlineSimCardDownload
              size={20}
              color="#737373"
              className="ml-2"
            />
          </div>
        );

      default:
        break;
    }
  };

  const getIsActionSelected = (action) => {
    switch (action) {
      case "dates":
        return true;
      case "grade":
        return gradeValue[0] !== 0 || gradeValue[1] !== 10;
      case "departments":
        return pageFilterToggled.includes("department");
      case "operators":
        return pageFilterToggled.includes("operators");
      case "subjects":
        return pageFilterToggled.includes("subject");
      case "call_direction":
        return pageFilterToggled.includes("call_direction");
      case "call_duration":
        return pageFilterToggled.includes("call_duration");
      default:
        return false;
    }
  };

  const renderActions = () => {
    const actions = [
      "grade",
      "departments",
      "operators",
      "subjects",
      "call_direction",
      "call_duration",
      "dates",
      "share",
      "export",
      "export_all",
    ];

    return (
      <div className="flex items-center gap-[8px] justify-end flex-wrap">
        {actions.map((action, actionIndex) => {
          const isSelected = getIsActionSelected(action);
          return (
            <div
              key={`a-${actionIndex}`}
              className={`rounded-md ${
                isSelected
                  ? "bg-[#4318ff]/[0.18] text-primary"
                  : "border-1 border-solid border-[#E5E5E5] text-[#404040]/[0.6] bg-white hover:bg-[#F5F5F5]"
              }`}
            >
              {renderAction(action)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex w-full items-center p-1.5 px-2 mt-[16px] bg-white rounded-md">
      <Input
        placeholder="Search"
        onClear={() => setSearchText("")}
        radius="full"
        value={searchText}
        startContent={
          <IoSearch
            color="#737373"
            className="w-4 h-4"
          />
        }
        className="flex-1 min-w-[350px] grow-1"
        classNames={{
          inputWrapper: ["bg-white border-none rounded-md shadow-none"],
        }}
        onChange={(e) => setSearchText(e.target.value)}
      />
      {renderActions()}
    </div>
  );
};

export default CallSearchBar;
